import { useEffect, useState } from 'react';
import { Dispatch, connect } from 'react-redux';
import { AdminActionCenterActionCreators, AppActionCreators, IAdminActionCenterComplianceResponse, IAdminActionCenterOtherResponse, IAdminActionCenterToDoResponse, IAdminNotificationListResponse, IAppState, IApplicationState } from '@nkr/store/index';
import { IAdminActionCenterCompliance, IAdminActionCenterOther, IAdminActionCenterToDo, IAdminNotification } from '@nkr/models/index';
import { Col, Row } from 'react-bootstrap';
import Notifications from './notifications';
import ToDo from '@nkr/views/center-admin/action-center/to-do/todo-counts-only';
import Compliance from '@nkr/views/center-admin/action-center/to-do/compliance-counts-only';
import Other from '@nkr/views/center-admin/action-center/to-do/other-counts-only';
import { ActionItem } from '@nkr/views/center-admin/action-center/models';
import { MainType, sectionList } from '@nkr/views/center-admin/action-center/to-do/models';
import { ContainerWrapper } from '@nkr/views/center-admin/action-center/to-do/styles';
import { actionItemMap } from '@nkr/views/center-admin/action-center/models/action-item-map';
import ToDoList from '@nkr/views/center-admin/action-center/to-do/lists/to-do-list';

interface IList {
    show: boolean,
    type: ActionItem,
    items: any[],
    title: string,
    mainType: MainType
}

const initialList: IList = {
    show: false,
    type: ActionItem.MatchOffers,
    items: [],
    title: '',
    mainType: MainType.ToDo
};

interface IOwnProps { }

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<any>,
    hideLoading: () => void,
    getNotifications: () => Promise<IAdminNotificationListResponse>,
    getToDoItems: () => Promise<IAdminActionCenterToDoResponse>,
    getComplianceItems: () => Promise<IAdminActionCenterComplianceResponse>,
    getOtherItems: () => Promise<IAdminActionCenterOtherResponse>,
    getMatchOffers: () => Promise<any>,
    getDonorClearance: () => Promise<any>,
    getPreselectsOutstanding: () => Promise<any>,
    getRequestsForDonorReview: () => Promise<any>,
    getXmResultsDue: (date: string) => Promise<any>,
    getConfirmReimbursementDonations: () => Promise<any>,
    getMissingVoucherHolders: () => Promise<any>,
    getFlaggedForVoucherDonation: () => Promise<any>,
    getAbWaitlistPatients: () => Promise<any>,
    getDashToDo: () => Promise<any>,
    markNotificationRead: (id: number) => Promise<any>,
    getDonorsMissingLdPriorization: () => Promise<any>,
    getPendingPatientIntakeReferrals: () => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

const ActionCenter = (props: TProps) => {
    const [showToDoLoading, setShowToDoLoading] = useState(true);
    const [todo, setToDo] = useState<IAdminActionCenterToDo>(null);

    const [showComplianceLoading, setShowComplianceLoading] = useState(true);
    const [compliance, setCompliance] = useState<IAdminActionCenterCompliance>(null);

    const [showOtherLoading, setShowOtherLoading] = useState(true);
    const [other, setOther] = useState<IAdminActionCenterOther>(null);

    const [showNotificationsLoading, setShowNotificationsLoading] = useState(true);
    const [notifications, setNotifications] = useState<IAdminNotification[]>([]);

    const [list, setList] = useState<IList>(initialList);

    const getToDoItems = (): Promise<IAdminActionCenterToDoResponse> => {
        return props.getToDoItems().then(response => {
            setShowToDoLoading(false);
            setToDo(response.data.results);
            return response;
        });
    };

    const getComplianceItems = (): Promise<IAdminActionCenterComplianceResponse> => {
        return props.getComplianceItems().then(response => {
            setShowComplianceLoading(false);
            setCompliance(response.data.results);
            return response;
        });
    };

    const getOtherItems = (): Promise<IAdminActionCenterOtherResponse> => {
        return props.getOtherItems().then(response => {
            setShowOtherLoading(false);
            setOther(response.data.results);
            return response;
        });
    };

    const getNotifications = (): Promise<IAdminNotificationListResponse> => {
        return props.getNotifications().then(response => {
            setShowNotificationsLoading(false);
            setNotifications(response.data.results);
            return response;
        });
    };

    const getActionMap = () => {
        const list: { id: ActionItem, action: any }[] = [
            { id: ActionItem.MatchOffers, action: props.getMatchOffers },
            { id: ActionItem.DonorClearance, action: props.getDonorClearance },
            { id: ActionItem.Preselects, action: props.getPreselectsOutstanding },
            { id: ActionItem.RequestForDonorReview, action: props.getRequestsForDonorReview },
            { id: ActionItem.XMResults, action: props.getXmResultsDue },
            { id: ActionItem.ConfirmReimbursementDonations, action: props.getConfirmReimbursementDonations },
            { id: ActionItem.MissingVoucherHolders, action: props.getMissingVoucherHolders },
            { id: ActionItem.HasAbWaitListPatients, action: props.getAbWaitlistPatients },
            { id: ActionItem.DashContactDue, action: props.getDashToDo },
            { id: ActionItem.FlaggedForVoucherDonation, action: props.getFlaggedForVoucherDonation },
            { id: ActionItem.LdPrioritization, action: props.getDonorsMissingLdPriorization },
            { id: ActionItem.PendingPatientIntakeReferrals, action: props.getPendingPatientIntakeReferrals }
        ];

        return list;
    }

    useEffect(() => {
        Promise.all([
            getNotifications(),
            getToDoItems(),
            getComplianceItems(),
            getOtherItems()
        ]);
    }, []);

    const handleItemClick = async (mainType: MainType, subType: ActionItem) => {
        let mainItem = sectionList.find(x => x.id == mainType).desc;
        let subItem = actionItemMap(0).find(x => x.id == subType);
        let adminItem = getActionMap().find(x => x.id == subType);

        if (adminItem == null) {
            alert('Unfortunately, that item is not available yet... please check back soon...');
            return;
        }

        props.showLoading('One moment please...').then(async () => {
            let response = await adminItem.action();
            let list = response.data.results;

            setList({
                show: true,
                type: subType,
                items: list,
                title: `${mainItem} - ${subItem.description}`,
                mainType
            });

            props.hideLoading();
        });
    }

    const handleListClose = () => {
        setList(initialList);
    };

    const handleMarkRead = (id: number) => {
        props.showLoading('One moment please...').then(() => {
            props.markNotificationRead(id)
                .then(() => {
                    getNotifications();
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    return (
        <>
            <ContainerWrapper className='container-fluid'>
                <h2 style={{ marginBottom: 30 }}>OPS ACTION CENTER</h2>

                <Row>
                    <Col xs={12} lg={12}>
                        <Notifications
                            data={notifications}
                            showLoading={showNotificationsLoading}
                            onMarkRead={handleMarkRead} />
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} lg={12}>
                        <ToDo
                            data={todo}
                            showLoading={showToDoLoading}
                            hideEmptyItems={false}
                            isAdmin={true}
                            onItemClick={handleItemClick} />
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} lg={12}>
                        <Compliance
                            data={compliance}
                            showLoading={showComplianceLoading}
                            hideEmptyItems={false}
                            isAdmin={true}
                            onItemClick={handleItemClick} />
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} lg={12}>
                        <Other
                            data={other}
                            showLoading={showOtherLoading}
                            hideEmptyItems={false}
                            isAdmin={true}
                            onItemClick={handleItemClick} />
                    </Col>
                </Row>
            </ContainerWrapper>

            <ToDoList
                show={list.show && list.type != ActionItem.PendingRecipientReferrals}
                type={list.type}
                title={list.title}
                items={list.items}
                logistics={null}
                coordinators={null}
                remoteStatus={null}
                centerId={0}
                mainType={list.mainType}
                onGetLogistics={() => null}
                onUploadFinalXm={() => null}
                onUpdateInternalDirect={() => null}
                onConfirmBloodDraw={() => null}
                onConfirmBloodDrawCompletion={() => null}
                onSave={() => null}
                onClose={handleListClose}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState, ownProps: IOwnProps): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getNotifications: () => {
            return dispatch(AdminActionCenterActionCreators.getNotifications());
        },
        getToDoItems: () => {
            return dispatch(AdminActionCenterActionCreators.getToDoItems());
        },
        getComplianceItems: () => {
            return dispatch(AdminActionCenterActionCreators.getComplianceItems());
        },
        getOtherItems: () => {
            return dispatch(AdminActionCenterActionCreators.getOtherItems());
        },
        getMatchOffers: () => {
            return dispatch(AdminActionCenterActionCreators.getMatchOffers());
        },
        getDonorClearance: () => {
            return dispatch(AdminActionCenterActionCreators.getDonorClearance());
        },
        getPreselectsOutstanding: () => {
            return dispatch(AdminActionCenterActionCreators.getPreselectsOutstanding());
        },
        getRequestsForDonorReview: () => {
            return dispatch(AdminActionCenterActionCreators.getRequestsForDonorReview());
        },
        getXmResultsDue: (date) => {
            return dispatch(AdminActionCenterActionCreators.getXmResultsDue(date));
        },
        getConfirmReimbursementDonations: () => {
            return dispatch(AdminActionCenterActionCreators.getConfirmReimbursementDonations());
        },
        getMissingVoucherHolders: () => {
            return dispatch(AdminActionCenterActionCreators.getMissingVoucherHolders());
        },
        getFlaggedForVoucherDonation: () => {
            return dispatch(AdminActionCenterActionCreators.getFlaggedForVoucherDonation());
        },
        getAbWaitlistPatients: () => {
            return dispatch(AdminActionCenterActionCreators.getAbWaitlistPatients());
        },
        getDashToDo: () => {
            return dispatch(AdminActionCenterActionCreators.getDashToDo());
        },
        getDonorsMissingLdPriorization: () => {
            return dispatch(AdminActionCenterActionCreators.getDonorsMissingLdPriorization());
        },
        getPendingPatientIntakeReferrals: () => {
            return dispatch(AdminActionCenterActionCreators.getPendingPatientIntakeReferrals());
        },
        markNotificationRead: (id) => {
            return dispatch(AdminActionCenterActionCreators.markNotificationRead(id));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(ActionCenter);